// frontend/src/ts/protocols/persistence/writeStore.ts
import {isTauriAvailable} from '@/common/utils/tauri'
import {PendingSummary, PendingWrite, WriteType} from './types'
import {TauriWriteStore} from './tauriWriteStore'
import {BrowserWriteStore} from './browserWriteStore'

export interface EnqueueArgs {
  companyId: string
  protocolId: string
  coalesceKey: string | null
  stepId: string | null
  writeType: WriteType
  payload: Record<string, unknown>
  /**
   * Optional user-edit delta for step/protocol updates. Null for
   * event_create / event_delete (the whole payload is the change).
   */
  changes?: Record<string, unknown> | null
}

export interface ClaimArgs {
  companyId: string
  protocolId: string
  backoffCutoffMs: number
  limit: number
}

export interface GetPendingWritesArgs {
  companyId: string
  protocolId: string
  coalesceView: boolean
  stateFilter: string[]
  limit?: number
}

/**
 * Backend-agnostic write-ahead-log interface. Two implementations:
 * - TauriWriteStore: SQLite-backed via NDC Tauri commands.
 * - BrowserWriteStore: localStorage primary, in-memory fallback.
 *
 * The coordinator, drainer, and write-status store call into this
 * interface and never invoke Tauri commands directly.
 */
export interface WriteStore {
  enqueue(args: EnqueueArgs): Promise<number>
  claim(args: ClaimArgs): Promise<PendingWrite[]>
  markPersisted(companyId: string, protocolId: string, id: number): Promise<void>
  markFailed(companyId: string, protocolId: string, id: number, error: string, retryable: boolean): Promise<void>
  retryWrite(companyId: string, protocolId: string, id: number): Promise<void>
  /**
   * Bulk transition of every dead row back to pending. Used by the
   * "Retry all dead" affordance in the queue viewer modal. Returns
   * the list of row ids that were actually transitioned; rows that
   * were superseded between the snapshot and the transaction commit
   * are NOT in the list (the WHERE clause `state = 'dead'` skips
   * them). The caller uses this to surface "retried N, M were
   * already superseded".
   */
  retryAllDead(companyId: string, protocolId: string): Promise<number[]>
  summary(companyId: string, protocolId: string): Promise<PendingSummary>
  /**
   * Snapshot of pending rows for the given protocol, used by
   * `loadProtocol` to merge queued writes into the freshly-fetched
   * server response (so a refresh-mid-drain resumes with the user's
   * in-flight edits, not the stale server view).
   *
   * `coalesceView=true` returns the latest row per coalesce_key
   * (matching Rust `get_pending_writes` semantics). `stateFilter`
   * selects which states to include.
   */
  getPendingWrites(args: GetPendingWritesArgs): Promise<PendingWrite[]>
  /**
   * Subscribe to persist notifications for this protocol. The drainer
   * calls `notifyWritePersisted(companyId, protocolId, rowId,
   * coalesceKey, writeType, response)` after each successful dispatch.
   *
   * Handlers are session-only. The `writeType` lets subscribers branch
   * on the kind of write — for example, ProtocolRecorder removes the
   * entry from its local event list when an `event_delete` is
   * persisted, while `event_create` is a no-op (the client's id is
   * already canonical).
   */
  onWritePersisted(
    companyId: string,
    protocolId: string,
    handler: (rowId: number, coalesceKey: string | null, writeType: WriteType, response: unknown) => void,
  ): () => void
  /**
   * Internal channel invoked by the drainer after `markPersisted`.
   * Routes to handlers registered via `onWritePersisted`. Returns the
   * awaitable for any async handlers (the drainer does not await by
   * default but tests and observability tooling may).
   */
  notifyWritePersisted(
    companyId: string,
    protocolId: string,
    rowId: number,
    coalesceKey: string | null,
    writeType: WriteType,
    response: unknown,
  ): Promise<void>
}

let cached: WriteStore | null = null

/**
 * Returns the appropriate WriteStore for the current environment. Cached
 * after first call so stateful implementations (BrowserWriteStore's
 * in-memory cache) share state across all consumers.
 */
export function getWriteStore(): WriteStore {
  if (cached !== null) return cached
  cached = isTauriAvailable() ? new TauriWriteStore() : new BrowserWriteStore()
  return cached
}

/** Test hook: force a specific WriteStore (or restore default selection). */
export function __setWriteStoreForTests(store: WriteStore | null): void {
  cached = store
}
